import { Injectable } from "@angular/core";
import { DbDaoBase } from "../../../gyzmo-commons/dao/db/base/db.dao.base";
import { IdWordingDbDao } from "../../../gyzmo-commons/dao/db/idWording.db.dao";
import { isNullOrEmpty } from "../../../gyzmo-commons/helpers/null.helper";
import { AppSqlProvider } from "../../../gyzmo-commons/persistence/app.sql.provider";
import { LoggerService } from "../../../gyzmo-commons/services/logs/logger.service";
import { Equipment } from "../../models/equipment.model";
import { RegistrationDbDao } from "./registration.db.dao";

@Injectable({
    providedIn: "root",
})
export class EquipmentDbDao extends DbDaoBase<Equipment> {
    constructor(
        logger: LoggerService,
        private sqlProvider: AppSqlProvider,
        private registrationDbDao: RegistrationDbDao,
        private idWordingDbDao: IdWordingDbDao,
    ) {
        super(logger);
    }

    public async createIndexes(): Promise<void> {
        // Ajoutez ici la création des index si nécessaire
    }

    public createTable(): Promise<void> {
        const query = `
            CREATE TABLE IF NOT EXISTS ${ Equipment.TABLENAME } (
                id TEXT PRIMARY KEY,
                internalId TEXT,
                title TEXT,
                category TEXT,
                model TEXT,
                brand TEXT,
                registration TEXT,
                serialNumber TEXT,
                mileage NUMERIC,
                mileageDate TEXT,
                mileage2 NUMERIC,
                mileageDate2 TEXT,
                inspectionModel TEXT,
                energy1 TEXT,
                energy2 TEXT,
                counter1 TEXT,
                counter2 TEXT,
                modelTyreWear TEXT,
                modelBrakeWear TEXT,
                wear TEXT,
                customerWarrantyDate TEXT,
                plannedDateOut TEXT,
                manufacturerWarrantyDate TEXT,
                reference4 TEXT,
                reference5 TEXT,
                reference6 TEXT,
                type TEXT
            );
        `;
        return this.sqlProvider.query(query, [])
            .then(async () => {
                await this.createIndexes();
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    public delete(id: string): Promise<any> {
        const deleteQuery = `DELETE FROM ${ Equipment.TABLENAME } WHERE id = ?;`;
        return this.sqlProvider.query(deleteQuery, [id]);
    }

    public deleteAll(): Promise<any> {
        const deleteAllQuery = `DELETE FROM ${ Equipment.TABLENAME };`;
        return this.sqlProvider.query(deleteAllQuery, []);
    }

    public get(id: string, hydrate: boolean = false): Promise<Equipment> {
        if (isNullOrEmpty(id)) {
            return Promise.resolve(null);
        }
        const selectQuery = `SELECT * FROM ${ Equipment.TABLENAME } WHERE id = ?;`;
        return this.sqlProvider.query(selectQuery, [id])
            .then(data => {
                if (data.rows.length <= 0) {
                    return null;
                }
                let equipment: Equipment = this.rowToModel(data.rows[0]);
                let hydratationPromises = [];
                if (hydrate) {
                    hydratationPromises.push(this.registrationDbDao.get(equipment.registration.id, hydrate)
                        .then(value => {
                            equipment.registration = value;
                        }));
                    hydratationPromises.push(this.idWordingDbDao.get(equipment.category.id, hydrate)
                        .then(value => {
                            equipment.category = value;
                        }));
                    hydratationPromises.push(this.idWordingDbDao.get(equipment.brand.id, hydrate)
                        .then(value => {
                            equipment.brand = value;
                        }));
                    hydratationPromises.push(this.idWordingDbDao.get(equipment.type.id, hydrate)
                        .then(value => {
                            equipment.type = value;
                        }));
                    hydratationPromises.push(this.idWordingDbDao.get(equipment.inspectionModel.id, hydrate)
                        .then(value => {
                            equipment.inspectionModel = value;
                        }));
                    hydratationPromises.push(this.idWordingDbDao.get(equipment.counter1.id, hydrate)
                        .then(value => {
                            equipment.counter1 = value;
                        }));
                    hydratationPromises.push(this.idWordingDbDao.get(equipment.counter2.id, hydrate)
                        .then(value => {
                            equipment.counter2 = value;
                        }));
                }
                return Promise.all(hydratationPromises)
                    .then(() => {
                        return equipment;
                    });
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    public getTableName(): string {
        return Equipment.TABLENAME;
    }

    protected rowToModel(row: any): Equipment {
        let equipment = new Equipment();
        equipment.id = row.id;
        equipment.internalId = row.internalId;
        equipment.title = row.title;
        equipment.category.id = row.category;
        equipment.model = row.model;
        equipment.brand.id = row.brand;
        equipment.registration.id = row.registration;
        equipment.serialNumber = row.serialNumber;
        equipment.mileage = Number(row.mileage);
        equipment.mileageDate = row.mileageDate;
        equipment.mileage2 = Number(row.mileage2);
        equipment.mileageDate2 = row.mileageDate2;
        equipment.customerWarrantyDate = row.customerWarrantyDate;
        equipment.plannedDateOut = row.plannedDateOut;
        equipment.manufacturerWarrantyDate = row.manufacturerWarrantyDate;
        equipment.inspectionModel.id = row.inspectionModel;
        equipment.energy1 = JSON.parse(row.energy1);
        equipment.energy2 = JSON.parse(row.energy2);
        equipment.counter1.id = row.counter1;
        equipment.counter2.id = row.counter2;
        equipment.modelTyreWear = row.modelTyreWear;
        equipment.modelBrakeWear = row.modelBrakeWear;
        equipment.type.id = row.type;
        equipment.reference4 = row.reference4;
        equipment.reference5 = row.reference5;
        equipment.reference6 = row.reference6;
        equipment.wear = !isNullOrEmpty(row.wear) ? JSON.parse(row.wear) : [];
        return equipment;
    }

    public save(equipment: Equipment): Promise<Equipment> {
        let promises = [];
        if (equipment.registration) {
            promises.push(this.registrationDbDao.save(equipment.registration));
        }
        if (equipment.category) {
            promises.push(this.idWordingDbDao.save(equipment.category));
        }
        if (equipment.brand) {
            promises.push(this.idWordingDbDao.save(equipment.brand));
        }
        if (equipment.type) {
            promises.push(this.idWordingDbDao.save(equipment.type));
        }
        if (equipment.inspectionModel) {
            promises.push(this.idWordingDbDao.save(equipment.inspectionModel));
        }
        if (equipment.counter1) {
            promises.push(this.idWordingDbDao.save(equipment.counter1));
        }
        if (equipment.counter2) {
            promises.push(this.idWordingDbDao.save(equipment.counter2));
        }
        return Promise.all(promises)
            .then(() => {
                const query = `
                    INSERT OR REPLACE INTO ${ Equipment.TABLENAME } (
                        id, internalId, title, category, model, brand, registration, serialNumber,
                        mileage, mileageDate, mileage2, mileageDate2, inspectionModel,
                        energy1, energy2, counter1, counter2, modelTyreWear, modelBrakeWear, wear,
                        customerWarrantyDate, plannedDateOut, manufacturerWarrantyDate, reference4, reference5, reference6, type
                    ) VALUES (
                        ?, ?, ?, ?, ?, ?, ?, ?,
                        ?, ?, ?, ?, ?,
                        ?, ?, ?, ?, ?, ?, ?,
                        ?, ?, ?, ?, ?, ?, ?
                    );
                `;
                const params = [
                    equipment.id,
                    equipment.internalId,
                    equipment.title,
                    equipment.category ? equipment.category.id : null,
                    equipment.model,
                    equipment.brand ? equipment.brand.id : null,
                    equipment.registration ? equipment.registration.id : null,
                    equipment.serialNumber,
                    equipment.mileage,
                    equipment.mileageDate,
                    equipment.mileage2,
                    equipment.mileageDate2,
                    equipment.inspectionModel ? equipment.inspectionModel.id : null,
                    JSON.stringify(equipment.energy1),
                    JSON.stringify(equipment.energy2),
                    equipment.counter1 ? equipment.counter1.id : null,
                    equipment.counter2 ? equipment.counter2.id : null,
                    equipment.modelTyreWear,
                    equipment.modelBrakeWear,
                    JSON.stringify(equipment.wear),
                    equipment.customerWarrantyDate,
                    equipment.plannedDateOut,
                    equipment.manufacturerWarrantyDate,
                    equipment.reference4,
                    equipment.reference5,
                    equipment.reference6,
                    equipment.type ? equipment.type.id : null,
                ];
                return this.sqlProvider.query(query, params)
                    .then(() => equipment)
                    .catch(reason => {
                        this.logSqlError(reason);
                        return null;
                    });
            });
    }
}
